/**
 * English locale catalogs for the host app chrome (`apps/frontend/src/app/layout`).
 *
 * Each constant below is the canonical English shape; the matching Russian
 * catalog in `./ru.ts` must satisfy `typeof <enName>` exactly. Consumers pair
 * the catalog with `defineLocaleCatalog({ en, ru })` from `@/shared/i18n` and
 * resolve it through `useLocaleMessages(catalog)`.
 *
 * English is the source-of-truth shape — when a key disappears from `en`,
 * `ru` must drop the matching key or the type system will fail.
 */
export const appShellEn = {
  /**
   * Top-level shell pieces surfaced by `App.vue` (the visually-hidden page
   * heading + the keyboard hints in the status footer) and the navigation
   * aria-label on `AppNav.vue`.
   */
  page: {
    skipToMain: 'Skip to main content',
    primaryNav: 'Primary navigation',
  },
  heading: {
    planner: 'GvG Planner',
    calendar: 'Calendar',
    officers: 'Officers',
    onboarding: 'Onboarding',
    default: 'WWM GvG',
  },
  statusHints: {
    calendar: {
      day: 'Day',
      week: 'Week',
      currentWeek: 'Current week',
      closeCalendar: 'Close calendar',
    },
    planner: {
      drag: 'Drag to move',
      click: 'Click to edit',
      newPlayer: 'New player',
    },
  },
  /**
   * Header brand block (`AppHeader.vue`). Two strings — the brand title and
   * a short tagline — plus the contextual labels that flank the guild
   * selector strip.
   */
  header: {
    title: 'WWM GvG Planner',
    subtitle: 'Where Winds Meet — Guild command',
    raidPlanning: 'Raid planning',
    guildLabel: 'Guild',
    planningGuild: 'Planning guild',
    accessRequestFailed: 'Unable to request access to this guild.',
  },
  /**
   * `ProfileChip.vue` — the small login trigger that lives in the header.
   * The alt text combines the user's display name with the word "avatar".
   */
  profileChip: {
    avatarAlt: (displayName: string) => `${displayName} avatar`,
    login: 'Login with Discord',
  },
  /**
   * `ProfilePopover.vue` — the dialog that opens when the profile chip is
   * clicked. Includes the dialog label, section headings, the empty-state
   * line, the data-key labels, and the destructive logout button copy.
   */
  profilePopover: {
    dialogLabel: 'Your profile',
    identity: 'Profile identity',
    quickLinks: 'Quick links',
    officerOf: 'Officer of',
    noOfficer: 'No officer permissions.',
    userId: 'user_id',
    discordId: 'discord_id',
    avatarAlt: (displayName: string) => `${displayName} avatar`,
    logout: 'Log out',
  },
  /**
   * `RaidRecoveryBanner.vue` — the alert strip on the Calendar surface that
   * recovers from a `/:guildId/plan/:raidId` navigation to a raid that no longer exists.
   * The body string stitches the suggestion sentence together so the Russian
   * translation can keep a natural sentence order.
   */
  raidRecovery: {
    title: 'This raid is no longer available.',
    body: 'It may have been deleted or you may no longer have access. Browse the Calendar to choose another raid.',
    action: 'Browse Calendar',
  },
} as const
